// Campo de monto con comas de miles que achica la letra para que el número siempre quepa.
// Las comas se ponen mientras se teclea; al salir (o con Enter) vuelve al valor formateado.
import { useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import {
    caretAfterDigits,
    formatAmount,
    groupTypedAmount,
    parseAmount,
} from '@/shared/lib/format/amount';

/** Letra mínima a la que se achica un número largo, en px. */
const MIN_FONT_PX = 10;

export interface AmountInputProps {
    value: number;
    /** Cada tecla que deja un monto válido; no confirma nada. */
    onValueChange: (value: number) => void;
    /** Al salir del campo o con Enter: aquí se confirma el valor. */
    onCommit?: () => void;
    /** 0 para enteros (semanas); 2 para pesos con centavos. */
    decimals?: 0 | 2;
    id?: string;
    disabled?: boolean;
    'aria-label'?: string;
    className?: string;
}

export function AmountInput({
    value,
    onValueChange,
    onCommit,
    decimals = 2,
    id,
    disabled,
    className,
    ...props
}: AmountInputProps) {
    const [draft, setDraft] = useState<string | null>(null);
    const ref = useRef<HTMLInputElement>(null);
    const baseFont = useRef<number | null>(null);
    // Cursor que hay que reponer tras poner las comas: cuenta de dígitos antes de él.
    const pendingCaret = useRef<number | null>(null);
    // Enter ya confirmó: el blur que sigue no confirma otra vez.
    const committedByEnter = useRef(false);
    const shown = draft ?? formatAmount(value, decimals);

    // Achica la letra de px en px hasta que el contenido ya no se desborda.
    useLayoutEffect(() => {
        const el = ref.current;
        if (el === null) return;
        const fit = (): void => {
            el.style.fontSize = '';
            baseFont.current ??= parseFloat(getComputedStyle(el).fontSize) || 16;
            let size = baseFont.current;
            while (el.scrollWidth > el.clientWidth && size > MIN_FONT_PX) {
                size -= 1;
                el.style.fontSize = `${String(size)}px`;
            }
        };
        fit();
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(fit);
        observer.observe(el);
        return () => {
            observer.disconnect();
        };
    }, [shown]);

    useLayoutEffect(() => {
        const el = ref.current;
        if (el === null || pendingCaret.current === null) return;
        const at = caretAfterDigits(el.value, pendingCaret.current);
        pendingCaret.current = null;
        el.setSelectionRange(at, at);
    }, [shown]);

    return (
        <input
            ref={ref}
            type="text"
            inputMode={decimals === 0 ? 'numeric' : 'decimal'}
            autoComplete="off"
            id={id}
            disabled={disabled}
            value={shown}
            onFocus={() => {
                committedByEnter.current = false;
                setDraft(formatAmount(value, decimals));
            }}
            onChange={(e) => {
                const raw = e.target.value;
                const caret = e.target.selectionStart ?? raw.length;
                // Se cuenta sobre lo que queda tras limpiar: sin ceros a la izquierda ni punto en enteros.
                pendingCaret.current = groupTypedAmount(raw.slice(0, caret), decimals).replace(
                    /[^\d.]/g,
                    '',
                ).length;
                const grouped = groupTypedAmount(raw, decimals);
                setDraft(grouped);
                const parsed = parseAmount(grouped, decimals);
                if (parsed !== null) onValueChange(parsed);
            }}
            onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                committedByEnter.current = true;
                setDraft(null);
                onCommit?.();
                e.currentTarget.blur();
            }}
            onBlur={() => {
                setDraft(null);
                if (committedByEnter.current) {
                    committedByEnter.current = false;
                    return;
                }
                onCommit?.();
            }}
            className={cn(
                'rounded border border-border bg-background px-2 py-[5px] tabular-nums outline-none focus:border-ring disabled:opacity-50',
                className,
            )}
            {...props}
        />
    );
}
